import { v4 as uuidv4 } from 'uuid';
import type { DashboardDraft } from './types';

/**
 * Structural constants of the Dashboard editor. No display text here — labels are
 * resolved from i18n by the components, so this file stays language-agnostic
 * (same convention as `agent-skills/constants.ts`).
 */

/** Route segment of the section, under `accounts/:accountId`. */
export const DASHBOARDS_ROUTE_SEGMENT = 'dashboards';

/** Path of the creation form, relative to the section. */
export const NEW_DASHBOARD_SEGMENT = 'new';

/**
 * The editing surface, relative to a dashboard (or to `new`).
 *
 * `editor` and not `canvas` because the canvas is the **surface**, and the
 * read-only view draws the very same one — groups, pages and widgets, just not
 * editable. What the segment has to name is the **mode**, which is the only thing
 * that separates `dashboards/:id` from `dashboards/:id/editor`.
 */
export const EDITOR_SEGMENT = 'editor';

/** Route param that carries a saved dashboard's id. */
export const DASHBOARD_ID_PARAM = 'dashboardId';

/**
 * Cap of the dashboard name.
 *
 * The name is what tells two dashboards on the same table apart, so it is worth
 * a real field — but a long one is paid twice over later, in the canvas header
 * and in the Home card, where it has to truncate. The prototype settled on the
 * same number.
 */
export const DASHBOARD_NAME_MAX = 80;

/**
 * Cap of a group's title.
 *
 * Shorter than the dashboard's name: a group title is a heading inside the
 * canvas, and it shares its row with the group's menu — past this it truncates
 * instead of naming anything. Nothing in the PRD sets a number; this one is the
 * width the header can actually show.
 */
export const GROUP_TITLE_MAX = 60;

/** Schema version of the definitions this build writes (PRD §8: versioned + migrated). */
/**
 * Cap of a widget's title.
 *
 * Shorter than a group's: a widget's name sits in a card header that can be a
 * third of the canvas wide, so anything longer truncates rather than reads.
 */
export const WIDGET_TITLE_MAX = 48;

/**
 * Cap of a widget's description (SL-1665).
 *
 * Longer than the title because it is a sentence rather than a name, and still
 * capped: what it annotates is a card, and a paragraph beside a chart is read
 * by nobody.
 */
export const WIDGET_DESCRIPTION_MAX = 160;

export const DASHBOARD_DEFINITION_VERSION = 1;

/**
 * A brand-new dashboard.
 *
 * It is born **empty**: one full-width group ready to receive, and zero
 * pre-built widgets. That is a product rule, not an implementation detail —
 * nothing may look finished without being finished (PRD §7), and the prototype's
 * pre-seeded canvas is illustration only.
 *
 * The group and page ids are generated here so the canvas can address them
 * before anything is persisted.
 *
 * The first page and the first group are born **unnamed**, not with a default
 * label: this file must not carry display text, and a name baked in here would
 * be English for everyone. The tab bar (SL-1661) and the group header resolve a
 * replacement label from i18n instead — same rule the PRD sets for an untitled
 * group, which shows a replacement description and not a gap.
 */
export function createEmptyDraft(folderId: string | null = null): DashboardDraft {
  const groupId = uuidv4();
  return {
    name: '',
    folderId,
    sources: { mainTable: null, links: [] },
    groups: [{ id: groupId, widgetIds: [] }],
    pages: [{ id: uuidv4(), name: '', groupIds: [groupId] }],
    widgets: [],
    version: DASHBOARD_DEFINITION_VERSION,
  };
}

/**
 * DOM id of a group's section on the canvas.
 *
 * An id and not a ref map because "the view follows the group" (PRD Feature 2)
 * has to scroll a group **that was just created** — a node that does not exist
 * yet when the action fires, so there is no ref to have collected. The lookup
 * happens one frame later, when it does.
 */
export const groupSectionDomId = (groupId: string): string => `dashboard-group-${groupId}`;

/**
 * Columns a group's row is divided into.
 *
 * Twelve because it is the number that divides cleanly into the layouts people
 * actually ask for — halves, thirds and quarters of a row — which is why the
 * prototype's grid uses it too.
 */
export const WIDGET_COLUMNS = 12;

/** Height of one grid row, in pixels. A widget's height is a multiple of this. */
export const WIDGET_ROW_HEIGHT = 64;

/**
 * How much of the row a new widget takes: a third.
 *
 * ⚠️ **A deliberate departure from the PRD**, decided by Efraín on 2026-09-11.
 * Feature 3 says a widget is born "a ancho de fila" and it is the ticket's first
 * acceptance criterion, so `WIDGET_COLUMNS` is what the spec asks for. What
 * settled it was the screen: a widget with nothing in it, three rows tall and
 * the full width across, reads as a mistake rather than as an invitation — and
 * the resize handles are right there to widen it.
 *
 * A third and not a half because that is what the canvas was already doing when
 * the call was made: SL-1664's group laid widgets out with
 * `repeat(auto-fill, minmax(min(100%, 280px), 1fr))`, which gives a lone widget
 * one track of about a third. That was itself an accident — the comment above
 * that rule describes `auto-fit`, the one that *would* have filled the row — so
 * the number is worth re-checking with product before launch.
 */
export const WIDGET_DEFAULT_SPAN = 4;

/** Height a new widget is born with — tall enough to read as a chart's frame. */
export const WIDGET_DEFAULT_ROWS = 4;

/**
 * Narrowest a widget can be dragged to: a quarter of the row.
 *
 * Not a taste call — it is the width at which the card's own header stops
 * working. Grip, name and the `···` menu share one row, and below a quarter
 * there is no room left for the name between them.
 */
export const WIDGET_MIN_SPAN = 3;

/** Leftmost column of a group's grid, and where a widget lands when it is alone. */
export const WIDGET_FIRST_COLUMN = 1;

/** Topmost row. The grid has no last one: a group grows to fit what is in it. */
export const WIDGET_FIRST_ROW = 1;

/**
 * Shortest a widget can be dragged to.
 *
 * Three rows and not two for the same kind of reason as `WIDGET_MIN_SPAN`: below
 * this the card is its header and a sliver, and the body has no room left for
 * what an unconfigured widget has to say about itself.
 */
export const WIDGET_MIN_ROWS = 3;

/**
 * Tallest a widget can be dragged to.
 *
 * A cap and not an open range: a group is not scrollable — it grows to fit what
 * it holds — so a widget dragged to an absurd height would push everything below
 * it off the screen with no way back but another drag.
 */
export const WIDGET_MAX_ROWS = 12;

/**
 * DOM id of a widget's card on the canvas.
 *
 * Same reason as `groupSectionDomId`: what has to be brought into view is often
 * a card that does not exist yet when the action fires — the one just added, or
 * the copy a duplication just made.
 */
export const widgetCardDomId = (widgetId: string): string => `dashboard-widget-${widgetId}`;

/**
 * DOM id of a group's widget grid.
 *
 * A drop has to answer "which column did this land in", and that is a question
 * about pixels: the grid's own box, measured at the moment of the drop. dnd-kit
 * hands over the rect of what the pointer is **over** — a widget, or the group's
 * body — and neither is the grid, so the grid is found by id and measured.
 */
export const groupGridDomId = (groupId: string): string => `dashboard-grid-${groupId}`;
